import React, { useState, useEffect, useMemo } from 'react';
import { useCRM } from '../context/CRMContext';
import { db } from '../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, setDoc, getDoc,
} from 'firebase/firestore';
import { addLeaveMessage, markLeaveThreadSeen, updateUserProfile } from '../firebase/firestore';

// ─── Constants ────────────────────────────────────────────────────────────────
const LEAVE_TYPES = [
  { id: 'annual',   label: 'Annual Leave',  icon: '🏖️', color: '#7c3aed' },
  { id: 'sick',     label: 'Sick Leave',    icon: '🤒', color: '#ef4444' },
  { id: 'casual',   label: 'Casual Leave',  icon: '☕', color: '#f59e0b' },
  { id: 'unpaid',   label: 'Unpaid Leave',  icon: '📄', color: '#64748b' },
];
const TYPE_MAP = Object.fromEntries(LEAVE_TYPES.map(t => [t.id, t]));

const STATUS_STYLES = {
  pending:  { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
};

function daysBetween(from, to) {
  if (!from || !to) return 0;
  const a = new Date(from), b = new Date(to);
  return Math.max(1, Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1);
}

// A half-day request is always exactly 0.5 day, on a single date — otherwise
// fall back to the normal whole-day range calculation. Centralizing this so
// every place that creates/edits a request (the employee form, and the new
// admin edit tool) computes `days` the same way.
function computeRequestDays(from, to, isHalfDay) {
  if (isHalfDay) return from ? 0.5 : 0;
  return daysBetween(from, to);
}

// Pretty-prints a day count, including the 0.5 (half-day) case, e.g.
// "1 day", "2 days", "0.5 day".
function formatDays(n) {
  const num = Number(n) || 0;
  const str = Number.isInteger(num) ? String(num) : num.toFixed(1);
  return `${str} day${num === 1 ? '' : 's'}`;
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function isNationalHoliday(dateStr, holidays = []) {
  return holidays.some(h => h.date === dateStr);
}

// ─── Calendar helpers (powers the "My Leaves" calendar view below) ───────────
function toISODate(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Every date string from `from` to `to` (inclusive), so a multi-day leave
// request lights up every day it covers on the calendar, not just its
// start date.
function eachDateInRange(fromStr, toStr) {
  const out = [];
  if (!fromStr || !toStr) return out;
  let d = new Date(fromStr);
  const end = new Date(toStr);
  let guard = 0;
  while (d <= end && guard < 730) { // 2-year safety cap against bad data looping forever
    out.push(toISODate(d));
    d = new Date(d);
    d.setDate(d.getDate() + 1);
    guard++;
  }
  return out;
}

// Returns an array of weeks (each an array of 7 { date, inMonth } cells,
// Sunday-first) covering the given month, padded with the trailing days of
// the previous/next month so every week is a full row.
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) {
    cells.push(new Date(year, month, i - startWeekday + 1));
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1];
    cells.push(new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1));
  }
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7).map(d => ({ date: d, inMonth: d.getMonth() === month })));
  }
  return weeks;
}

// ─── Policy defaults ──────────────────────────────────────────────────────────
const DEFAULT_POLICY = {
  annualLeaves: 20,
  sickLeaves: 10,
  casualLeaves: 6,
  holidays: [],  // [{ date: 'YYYY-MM-DD', name: 'Holiday Name' }]
};

// ─── India's 2026 holiday calendar (predefined picker source) ─────────────────
// Three tiers, matching how the Indian government itself categorises these:
//  - 'national'  — the 3 days India's Constitution/convention actually calls
//                   "National Holidays" (Republic Day, Independence Day,
//                   Gandhi Jayanti). Checked by default.
//  - 'gazetted'   — the remaining Central Government Gazetted Holidays (all
//                   central govt offices/banks close). Also checked by
//                   default since most companies follow this calendar.
//  - 'restricted' — the much longer "Restricted Holiday" list (regional /
//                   community festivals; central govt staff may pick any 2).
//                   Unchecked by default — admins opt in per-company.
// Dates for Islamic festivals (Id-ul-Fitr, Id-ul-Zuha/Bakrid, Muharram,
// Id-e-Milad) follow the lunar calendar and are confirmed only on moon
// sighting — treat them as "tentative, ±1 day" until the government's final
// gazette notification for that month.
const INDIA_HOLIDAYS_2026 = [
  { date: '2026-01-26', name: 'Republic Day',              category: 'national' },
  { date: '2026-08-15', name: 'Independence Day',          category: 'national' },
  { date: '2026-10-02', name: 'Mahatma Gandhi Jayanti',     category: 'national' },

  { date: '2026-03-04', name: 'Holi',                       category: 'gazetted' },
  { date: '2026-03-21', name: 'Id-ul-Fitr (tentative)',     category: 'gazetted' },
  { date: '2026-03-26', name: 'Ram Navami',                 category: 'gazetted' },
  { date: '2026-03-31', name: 'Mahavir Jayanti',            category: 'gazetted' },
  { date: '2026-04-03', name: 'Good Friday',                category: 'gazetted' },
  { date: '2026-05-01', name: 'Buddha Purnima',             category: 'gazetted' },
  { date: '2026-05-27', name: 'Id-ul-Zuha / Bakrid (tentative)', category: 'gazetted' },
  { date: '2026-06-26', name: 'Muharram (tentative)',       category: 'gazetted' },
  { date: '2026-08-26', name: 'Id-e-Milad (tentative)',     category: 'gazetted' },
  { date: '2026-09-04', name: 'Janmashtami',                category: 'gazetted' },
  { date: '2026-10-20', name: 'Dussehra',                   category: 'gazetted' },
  { date: '2026-11-08', name: 'Diwali (Deepavali)',         category: 'gazetted' },
  { date: '2026-11-24', name: 'Guru Nanak Jayanti',         category: 'gazetted' },
  { date: '2026-12-25', name: 'Christmas Day',              category: 'gazetted' },

  { date: '2026-01-01', name: "New Year's Day",             category: 'restricted' },
  { date: '2026-01-14', name: 'Makar Sankranti / Pongal',   category: 'restricted' },
  { date: '2026-01-23', name: 'Vasant Panchami',            category: 'restricted' },
  { date: '2026-02-15', name: 'Maha Shivaratri',            category: 'restricted' },
  { date: '2026-03-19', name: 'Ugadi / Gudi Padwa',         category: 'restricted' },
  { date: '2026-04-05', name: 'Easter Sunday',              category: 'restricted' },
  { date: '2026-04-14', name: 'Vaisakhi / Bihu',            category: 'restricted' },
  { date: '2026-07-16', name: 'Rath Yatra',                 category: 'restricted' },
  { date: '2026-08-26', name: 'Onam',                       category: 'restricted' },
  { date: '2026-08-28', name: 'Raksha Bandhan',             category: 'restricted' },
  { date: '2026-09-14', name: 'Ganesh Chaturthi',           category: 'restricted' },
  { date: '2026-10-29', name: 'Karva Chauth',               category: 'restricted' },
  { date: '2026-11-09', name: 'Govardhan Puja',             category: 'restricted' },
  { date: '2026-11-11', name: 'Bhai Dooj',                  category: 'restricted' },
  { date: '2026-11-15', name: 'Chhath Puja',                category: 'restricted' },
];

const HOLIDAY_CATEGORY_LABELS = {
  national: 'National Holidays',
  gazetted: 'Other Gazetted Holidays',
  restricted: 'Popular Festivals & Regional Holidays (optional)',
};

// ─── Leave Balance Bar ────────────────────────────────────────────────────────
function BalanceBar({ used, total, color }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div style={{ height: 6, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden', marginTop: 6 }}>
      <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: 3, transition: 'width 0.4s ease' }} />
    </div>
  );
}

// ─── Leave & Holiday Calendar ─────────────────────────────────────────────────
// Month-grid view used inside "My Leaves" so an employee can see, at a
// glance, which upcoming days are company holidays (from the admin-saved
// policy) vs. their own approved/pending leave — this is the piece that was
// previously completely missing: holidays an admin selected in Policy never
// showed up anywhere on the employee-facing side.
function LeaveCalendar({ holidays = [], myLeaveDays = {} }) {
  const [cursor, setCursor] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const weeks = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const holidayByDate = useMemo(() => {
    const m = {};
    holidays.forEach(h => { m[h.date] = h.name; });
    return m;
  }, [holidays]);
  const todayIso = toISODate(new Date());

  const navBtn = {
    width: 30, height: 30, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc',
    cursor: 'pointer', fontSize: 15, fontWeight: 700, color: '#475569', lineHeight: 1,
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() - 1, 1))} style={navBtn}>‹</button>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
          📅 {cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <button onClick={() => setCursor(c => new Date(c.getFullYear(), c.getMonth() + 1, 1))} style={navBtn}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textAlign: 'center', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
          {week.map((cell, ci) => {
            const iso = toISODate(cell.date);
            const holidayName = holidayByDate[iso];
            const leaveInfo = myLeaveDays[iso];
            const isToday = iso === todayIso;
            const isWeekend = ci === 0 || ci === 6;
            const t = leaveInfo ? TYPE_MAP[leaveInfo.type] : null;
            return (
              <div key={ci}
                title={holidayName || (leaveInfo ? `${t?.label || 'Leave'} — ${leaveInfo.status}` : '')}
                style={{
                  minHeight: 56, borderRadius: 8, padding: '4px 5px', boxSizing: 'border-box',
                  background: holidayName ? '#fef3c7' : leaveInfo ? (leaveInfo.status === 'approved' ? '#ede9fe' : '#fff7ed') : (isWeekend ? '#f8fafc' : '#fff'),
                  border: isToday ? '2px solid #7c3aed' : '1px solid #f1f5f9',
                  opacity: cell.inMonth ? 1 : 0.35,
                }}>
                <div style={{ fontSize: 11, fontWeight: isToday ? 800 : 600, color: cell.inMonth ? '#0f172a' : '#cbd5e1' }}>
                  {cell.date.getDate()}
                </div>
                {holidayName && (
                  <div style={{ fontSize: 9, color: '#92400e', marginTop: 2, lineHeight: 1.25, fontWeight: 600 }}>
                    🎉 {holidayName}
                  </div>
                )}
                {leaveInfo && (
                  <div style={{ fontSize: 9, marginTop: 2, color: leaveInfo.status === 'approved' ? '#6d28d9' : '#c2410c', fontWeight: 700 }}>
                    {t?.icon} {leaveInfo.status === 'approved' ? 'On leave' : 'Pending'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#fef3c7', marginRight: 5, verticalAlign: 'middle' }} />Holiday</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#ede9fe', marginRight: 5, verticalAlign: 'middle' }} />Approved leave</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#fff7ed', marginRight: 5, verticalAlign: 'middle' }} />Pending leave</span>
      </div>
    </div>
  );
}

// ─── Leave Request Discussion Thread ──────────────────────────────────────────
// Reusable to-and-fro messaging thread attached to a single leave request,
// stored as a Firestore subcollection (companies/{id}/leaveRequests/{id}/messages)
// so either side can keep talking at any point — not just a single note
// captured once at approve/reject time.
function LeaveRequestThread({ company, requestId, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!company?.id || !requestId) return;
    const unsub = onSnapshot(
      collection(db, 'companies', company.id, 'leaveRequests', requestId, 'messages'),
      snap => {
        const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        msgs.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        setMessages(msgs);
      }
    );
    return unsub;
  }, [company?.id, requestId]);

  // Mark this thread "seen" by the current viewer's role as soon as it's opened,
  // so the unread flag the other side relies on (adminUnread/employeeUnread on
  // the parent leaveRequests/{requestId} doc) clears the moment they actually
  // read it — not just when they reply.
  useEffect(() => {
    if (!company?.id || !requestId || !currentUser?.id) return;
    const viewerRole = (currentUser.role === 'admin' || currentUser.role === 'superadmin') ? 'admin' : 'employee';
    markLeaveThreadSeen(company.id, requestId, viewerRole).catch(() => {});
  }, [company?.id, requestId, currentUser?.id, currentUser?.role]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || !company?.id || !requestId || !currentUser?.id || sending) return;
    setSending(true);
    try {
      const fromRole = (currentUser.role === 'admin' || currentUser.role === 'superadmin') ? 'admin' : 'employee';
      await addLeaveMessage(company.id, requestId, {
        fromUserId: currentUser.id,
        fromName: currentUser.name || currentUser.email,
        fromRole,
        text: trimmed,
      });
      setText('');
    } catch (e) { console.error(e); }
    setSending(false);
  };

  return (
    <div style={{ marginTop: 10, marginLeft: 36, padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto', marginBottom: 10 }}>
        {messages.length === 0 ? (
          <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', padding: 10 }}>No messages yet — start the conversation.</div>
        ) : messages.map(m => {
          const mine = m.senderId === currentUser?.id;
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '75%', padding: '8px 12px', borderRadius: 10,
                background: mine ? '#7c3aed' : '#fff',
                color: mine ? '#fff' : '#0f172a',
                border: mine ? 'none' : '1px solid #e2e8f0',
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.75, marginBottom: 2 }}>
                  {mine ? 'You' : m.senderName}{!mine && m.isAdmin ? ' (Admin)' : ''}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.4, wordBreak: 'break-word' }}>{m.text}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }}
          placeholder="Write a message…"
          style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, boxSizing: 'border-box' }} />
        <button onClick={send} disabled={sending || !text.trim()} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none', cursor: sending ? 'default' : 'pointer',
          background: 'var(--color-primary, #7c3aed)', color: '#fff', fontWeight: 700, fontSize: 12,
          opacity: sending || !text.trim() ? 0.6 : 1,
        }}>Send</button>
      </div>
    </div>
  );
}

// ─── My Leave Portal ──────────────────────────────────────────────────────────
function MyLeavePortal({ requests, policy, currentUser, onRequest }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'annual', from: '', to: '', reason: '', isHalfDay: false });
  const [saving, setSaving] = useState(false);
  const [openThread, setOpenThread] = useState(null);
  const [flagging, setFlagging] = useState(null);
  const { company } = useCRM();

  // Lets an employee push back on a leave entry the admin logged on their
  // behalf (or any entry at all) if they think it's wrong — e.g. wrong dates,
  // wrong type, or a leave they never actually took. This both opens the
  // existing discussion thread with a starter message AND sets a `disputed`
  // flag the admin's list surfaces prominently, so it doesn't just sit as an
  // easy-to-miss chat message. Mirrors the unread-flip pattern addLeaveMessage
  // already uses elsewhere in this file.
  const flagDispute = async (r) => {
    if (!company?.id || !currentUser?.id) return;
    setFlagging(r.id);
    try {
      await updateDoc(doc(db, 'companies', company.id, 'leaveRequests', r.id), {
        disputed: true,
        disputedAt: serverTimestamp(),
      });
      await addLeaveMessage(company.id, r.id, {
        fromUserId: currentUser.id,
        fromName: currentUser.name || currentUser.email,
        fromRole: 'employee',
        text: '🚩 I believe this leave entry is incorrect — please review.',
      });
      setOpenThread(r.id);
    } catch (e) { console.error(e); alert('Failed to flag this entry'); }
    setFlagging(null);
  };

  const myRequests = useMemo(() =>
    requests.filter(r => r.userId === currentUser?.id)
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)),
    [requests, currentUser]);

  // Calculate used leaves
  const usedByType = useMemo(() => {
    const totals = { annual: 0, sick: 0, casual: 0 };
    myRequests.filter(r => r.status === 'approved').forEach(r => {
      if (totals[r.type] !== undefined) totals[r.type] += r.days || 0;
    });
    return totals;
  }, [myRequests]);

  // Joining date (from user profile in Firestore or set by admin)
  const joiningDate = currentUser?.joiningDate
    ? formatDate(currentUser.joiningDate) : 'Not set';

  // Every date this employee's own non-rejected leave requests cover, keyed
  // by ISO date, so the calendar below can mark each day individually
  // (a multi-day request needs every day in its range lit up, not just the
  // start date). Approved wins over pending if a date is somehow in both.
  const myLeaveDaysMap = useMemo(() => {
    const map = {};
    myRequests.filter(r => r.status !== 'rejected').forEach(r => {
      eachDateInRange(r.from, r.to).forEach(iso => {
        if (!map[iso] || r.status === 'approved') map[iso] = { type: r.type, status: r.status };
      });
    });
    return map;
  }, [myRequests]);

  // Upcoming company holidays — shown as a standalone strip too (not just on
  // the calendar) so they're visible immediately without having to navigate
  // months. This is the piece that was previously missing entirely: an
  // admin could select holidays in Policy but employees had no way to see
  // them anywhere in their own "My Leaves" view.
  const todayIso = toISODate(new Date());
  const upcomingHolidays = useMemo(() =>
    (policy.holidays || [])
      .filter(h => h.date >= todayIso)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 6),
    [policy.holidays, todayIso]);

  const handleSubmit = async () => {
    const to = form.isHalfDay ? form.from : form.to;
    if (!form.from || !to || !form.reason.trim()) return alert('Please fill all fields');
    if (!company?.id || !currentUser?.id) return;
    setSaving(true);
    try {
      const days = computeRequestDays(form.from, to, form.isHalfDay);
      await addDoc(collection(db, 'companies', company.id, 'leaveRequests'), {
        userId: currentUser.id,
        userName: currentUser.name || currentUser.email,
        userEmail: currentUser.email,
        type: form.type,
        from: form.from,
        to,
        days,
        isHalfDay: !!form.isHalfDay,
        reason: form.reason.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
        // A brand-new application is, by definition, something the admin
        // hasn't seen yet — flip the same flag the discussion-thread helper
        // (addLeaveMessage) uses, so the admin's notification bell and the
        // requests list both pick it up via one consistent signal.
        adminUnread: true,
      });
      setForm({ type: 'annual', from: '', to: '', reason: '', isHalfDay: false });
      setShowForm(false);
    } catch (e) { console.error(e); alert('Failed to submit request'); }
    setSaving(false);
  };

  const daysCalc = form.isHalfDay
    ? (form.from ? 0.5 : 0)
    : (form.from && form.to ? daysBetween(form.from, form.to) : 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>My Leave Portal</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Joined: {joiningDate}
          </div>
        </div>
        <button onClick={() => setShowForm(s => !s)} style={{
          padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
          background: showForm ? '#f1f5f9' : 'var(--color-primary, #7c3aed)', color: showForm ? '#64748b' : '#fff',
          fontWeight: 700, fontSize: 13,
        }}>
          {showForm ? '✕ Cancel' : '+ Request Leave'}
        </button>
      </div>

      {/* Leave Balances */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { id: 'annual', total: policy.annualLeaves ?? 20, color: '#7c3aed', label: 'Annual Leave' },
          { id: 'sick',   total: policy.sickLeaves   ?? 10, color: '#ef4444', label: 'Sick Leave'   },
          { id: 'casual', total: policy.casualLeaves ?? 6,  color: '#f59e0b', label: 'Casual Leave'  },
        ].map(t => {
          const used = usedByType[t.id] || 0;
          const remaining = Math.max(0, t.total - used);
          return (
            <div key={t.id} style={{
              background: '#fff', borderRadius: 14, padding: '18px 20px',
              border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{t.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: t.color }}>{remaining}</span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>/ {t.total} days left</span>
              </div>
              <BalanceBar used={used} total={t.total} color={t.color} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{used} used · {t.total} total</div>
            </div>
          );
        })}
      </div>

      {/* Upcoming Holidays strip */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>🎉 Upcoming Holidays</div>
        {upcomingHolidays.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>No upcoming holidays set by your admin yet.</div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {upcomingHolidays.map((h, i) => (
              <div key={h.date + i} style={{
                display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 14px',
                borderRadius: 10, background: '#fef3c7', minWidth: 120,
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{h.name}</span>
                <span style={{ fontSize: 11, color: '#92400e' }}>{formatDate(h.date)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Calendar — holidays + my own approved/pending leave, by month */}
      <LeaveCalendar holidays={policy.holidays || []} myLeaveDays={myLeaveDaysMap} />

      {/* Request Form */}
      {showForm && (
        <div style={{
          background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #e2e8f0',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 24,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 18 }}>New Leave Request</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>Leave Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc' }}>
                {LEAVE_TYPES.filter(t => t.id !== 'unpaid').map(t => (
                  <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>From</label>
              <input type="date" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value, to: f.isHalfDay ? e.target.value : f.to }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>
                To {daysCalc > 0 && <span style={{ color: '#7c3aed', fontWeight: 700 }}>({formatDays(daysCalc)})</span>}
              </label>
              <input type="date" value={form.isHalfDay ? form.from : form.to} min={form.from} disabled={form.isHalfDay}
                onChange={e => setForm(f => ({ ...f, to: e.target.value }))}
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13,
                  background: form.isHalfDay ? '#f1f5f9' : '#f8fafc', boxSizing: 'border-box', color: form.isHalfDay ? '#94a3b8' : 'inherit',
                }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isHalfDay}
                onChange={e => setForm(f => ({ ...f, isHalfDay: e.target.checked, to: e.target.checked ? f.from : f.to }))} />
              Half day (0.5 day) — only available for a single date
            </label>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 5 }}>Reason</label>
            <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="Brief reason for leave..." rows={3}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleSubmit} disabled={saving} style={{
              padding: '10px 24px', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: 'var(--color-primary, #7c3aed)', color: '#fff', fontWeight: 700, fontSize: 13,
            }}>{saving ? 'Submitting…' : 'Submit Request'}</button>
            <button onClick={() => setShowForm(false)} style={{
              padding: '10px 20px', borderRadius: 9, border: '1px solid #e2e8f0', cursor: 'pointer',
              background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: 13,
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* My Leave History */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
          My Leave History
        </div>
        {myRequests.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No leave requests yet
          </div>
        ) : myRequests.map(r => {
          const t = TYPE_MAP[r.type] || TYPE_MAP.annual;
          const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
          const isOpen = openThread === r.id;
          return (
            <div key={r.id} style={{ padding: '14px 20px', borderBottom: '1px solid #f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ fontSize: 22 }}>{t.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                    {t.label}{r.isHalfDay && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>HALF DAY</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {formatDate(r.from)}{r.from !== r.to ? ` → ${formatDate(r.to)}` : ''} · {formatDays(r.days)}
                  </div>
                  {r.reason && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.reason}</div>}
                  {r.adminNote && (
                    <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 3, fontStyle: 'italic' }}>
                      Admin: {r.adminNote}
                    </div>
                  )}
                  {r.loggedByAdmin && (
                    <div style={{ fontSize: 10, color: '#92400e', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 20, fontWeight: 700, fontSize: 10,
                        background: '#fef3c7', color: '#92400e',
                      }}>📝 Logged by {r.loggedByAdminName || 'admin'}</span>
                      {r.disputed && (
                        <span style={{
                          padding: '2px 8px', borderRadius: 20, fontWeight: 700, fontSize: 10,
                          background: '#fee2e2', color: '#dc2626',
                        }}>🚩 Disputed — awaiting review</span>
                      )}
                    </div>
                  )}
                </div>
                <span style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: s.bg, color: s.color,
                }}>{s.label}</span>
                {r.loggedByAdmin && !r.disputed && (
                  <button onClick={() => flagDispute(r)} disabled={flagging === r.id} title="Tell your admin this entry looks wrong"
                    style={{
                      padding: '5px 12px', borderRadius: 8, border: '1px solid #fecaca', cursor: 'pointer',
                      background: '#fff5f5', color: '#dc2626', fontWeight: 600, fontSize: 11,
                    }}>
                    {flagging === r.id ? 'Flagging…' : '🚩 Flag as incorrect'}
                  </button>
                )}
                <button onClick={() => setOpenThread(isOpen ? null : r.id)} style={{
                  position: 'relative',
                  padding: '5px 12px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
                  background: isOpen ? '#ede9fe' : '#f8fafc', color: isOpen ? '#7c3aed' : '#64748b', fontWeight: 600, fontSize: 11,
                }}>
                  💬 {isOpen ? 'Hide' : 'Discuss'}
                  {r.employeeUnread && !isOpen && (
                    <span style={{
                      position: 'absolute', top: -3, right: -3, width: 9, height: 9,
                      borderRadius: '50%', background: '#ef4444', border: '1.5px solid #fff',
                    }} />
                  )}
                </button>
              </div>
              {isOpen && (
                <LeaveRequestThread company={company} requestId={r.id} currentUser={currentUser} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Admin Approval Panel ─────────────────────────────────────────────────────
function AdminApprovalPanel({ requests, policy, setPolicy, company, teamMembers, currentUser }) {
  const [tab, setTab] = useState('pending'); // pending | all | policy
  const [adminNote, setAdminNote] = useState({});
  const [openThread, setOpenThread] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null); // { id, from, to, type, isHalfDay }
  const [savingEdit, setSavingEdit] = useState(false);

  const pending  = requests.filter(r => r.status === 'pending');
  const approved = requests.filter(r => r.status === 'approved');
  const rejected = requests.filter(r => r.status === 'rejected');
  const displayed = tab === 'pending' ? pending : tab === 'approved' ? approved : tab === 'rejected' ? rejected : requests;

  const handleAction = async (reqId, status, note) => {
    if (!company?.id) return;
    await updateDoc(doc(db, 'companies', company.id, 'leaveRequests', reqId), {
      status,
      adminNote: note || '',
      reviewedAt: serverTimestamp(),
      // Acting on the request is the admin "seeing" it — clear their unread
      // flag, and since a decision is news the employee hasn't heard yet,
      // flag it unread for them (mirrors addLeaveMessage's flip-the-other-
      // side behavior for ordinary thread replies).
      adminUnread: false,
      employeeUnread: true,
    });
    setAdminNote(n => { const c = { ...n }; delete c[reqId]; return c; });
  };

  // Lets an admin correct a request's recorded type/dates/half-day flag after
  // the fact — e.g. fixing a past leave that was wrongly logged as a full day
  // when it was actually a half day, which is the most common way a leave
  // balance (especially Casual Leave) ends up looking wrong.
  const startEdit = (r) => setEditingRequest({
    id: r.id, from: r.from, to: r.to, type: r.type, isHalfDay: !!r.isHalfDay,
  });

  const saveEdit = async () => {
    if (!editingRequest || !company?.id) return;
    const to = editingRequest.isHalfDay ? editingRequest.from : editingRequest.to;
    if (!editingRequest.from || !to) return alert('Please fill in the dates');
    setSavingEdit(true);
    try {
      const days = computeRequestDays(editingRequest.from, to, editingRequest.isHalfDay);
      await updateDoc(doc(db, 'companies', company.id, 'leaveRequests', editingRequest.id), {
        type: editingRequest.type,
        from: editingRequest.from,
        to,
        days,
        isHalfDay: !!editingRequest.isHalfDay,
      });
      setEditingRequest(null);
    } catch (e) { console.error(e); alert('Failed to save changes'); }
    setSavingEdit(false);
  };

  // Clears the 🚩 flag an employee raised once the admin has reviewed it —
  // either by fixing the entry with Edit above, or by confirming with the
  // employee via Discuss that it was actually correct as logged.
  const resolveDispute = async (reqId) => {
    if (!company?.id) return;
    try {
      await updateDoc(doc(db, 'companies', company.id, 'leaveRequests', reqId), {
        disputed: false,
        employeeUnread: true,
      });
    } catch (e) { console.error(e); alert('Failed to clear dispute flag'); }
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>Leave Management</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 22 }}>Manage team leaves, policy, and national holidays</div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 22, background: '#f1f5f9', borderRadius: 10, padding: 5, width: 'fit-content' }}>
        {[
          { id: 'pending',  label: `Pending (${pending.length})` },
          { id: 'approved', label: 'Approved' },
          { id: 'rejected', label: 'Rejected' },
          { id: 'all',      label: 'All Requests' },
          { id: 'policy',   label: '⚙️ Policy' },
          { id: 'team',     label: '👥 Team' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            background: tab === t.id ? '#fff' : 'transparent',
            color: tab === t.id ? '#0f172a' : '#64748b',
            boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Policy tab */}
      {tab === 'policy' && (
        <PolicyEditor policy={policy} setPolicy={setPolicy} company={company} />
      )}

      {/* Team tab */}
      {tab === 'team' && (
        <TeamLeaveView teamMembers={teamMembers} requests={requests} policy={policy} company={company} currentUser={currentUser} />
      )}

      {/* Requests list */}
      {tab !== 'policy' && tab !== 'team' && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {displayed.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No {tab === 'all' ? '' : tab} requests
            </div>
          ) : displayed.map(r => {
            const t = TYPE_MAP[r.type] || TYPE_MAP.annual;
            const s = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
            const isThreadOpen = openThread === r.id;
            const isEditing = editingRequest?.id === r.id;
            return (
              <div key={r.id} style={{ padding: '16px 22px', borderBottom: '1px solid #f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ fontSize: 22, marginTop: 2 }}>{t.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{r.userName || r.userEmail}</span>
                      {r.adminUnread && (
                        <span style={{
                          padding: '1px 8px', borderRadius: 20, fontSize: 9, fontWeight: 800,
                          letterSpacing: 0.4, background: '#fee2e2', color: '#dc2626',
                        }}>NEW</span>
                      )}
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>·</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: t.color }}>{t.label}</span>
                      <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>
                      {r.isHalfDay && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e' }}>HALF DAY</span>}
                      {r.loggedByAdmin && (
                        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#f1f5f9', color: '#64748b' }}>
                          📝 Logged by {r.loggedByAdminName || 'admin'}
                        </span>
                      )}
                      {r.disputed && (
                        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 800, background: '#fee2e2', color: '#dc2626' }}>
                          🚩 DISPUTED — employee flagged this as incorrect
                        </span>
                      )}
                    </div>

                    {isEditing ? (
                      <div style={{ marginTop: 8, padding: 12, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                          <select value={editingRequest.type} onChange={e => setEditingRequest(er => ({ ...er, type: e.target.value }))}
                            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}>
                            {LEAVE_TYPES.map(lt => <option key={lt.id} value={lt.id}>{lt.icon} {lt.label}</option>)}
                          </select>
                          <input type="date" value={editingRequest.from}
                            onChange={e => setEditingRequest(er => ({ ...er, from: e.target.value, to: er.isHalfDay ? e.target.value : er.to }))}
                            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                          <input type="date" value={editingRequest.isHalfDay ? editingRequest.from : editingRequest.to}
                            min={editingRequest.from} disabled={editingRequest.isHalfDay}
                            onChange={e => setEditingRequest(er => ({ ...er, to: e.target.value }))}
                            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, background: editingRequest.isHalfDay ? '#f1f5f9' : '#fff' }} />
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                            <input type="checkbox" checked={editingRequest.isHalfDay}
                              onChange={e => setEditingRequest(er => ({ ...er, isHalfDay: e.target.checked, to: e.target.checked ? er.from : er.to }))} />
                            Half day
                          </label>
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
                          New total: <strong>{formatDays(computeRequestDays(editingRequest.from, editingRequest.isHalfDay ? editingRequest.from : editingRequest.to, editingRequest.isHalfDay))}</strong>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={saveEdit} disabled={savingEdit} style={{
                            padding: '6px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12,
                          }}>{savingEdit ? 'Saving…' : 'Save changes'}</button>
                          <button onClick={() => setEditingRequest(null)} style={{
                            padding: '6px 16px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
                            background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 12,
                          }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {formatDate(r.from)}{r.from !== r.to ? ` → ${formatDate(r.to)}` : ''} · <strong>{formatDays(r.days)}</strong>
                      </div>
                    )}

                    {r.reason && <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>Reason: {r.reason}</div>}
                    {r.adminNote && <div style={{ fontSize: 12, color: '#7c3aed', marginTop: 3 }}>Note: {r.adminNote}</div>}

                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => setOpenThread(isThreadOpen ? null : r.id)} style={{
                        position: 'relative',
                        padding: '4px 12px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
                        background: isThreadOpen ? '#ede9fe' : '#f8fafc', color: isThreadOpen ? '#7c3aed' : '#64748b', fontWeight: 600, fontSize: 11,
                      }}>
                        💬 {isThreadOpen ? 'Hide' : 'Discuss'}
                        {r.adminUnread && !isThreadOpen && (
                          <span style={{
                            position: 'absolute', top: -3, right: -3, width: 9, height: 9,
                            borderRadius: '50%', background: '#ef4444', border: '1.5px solid #fff',
                          }} />
                        )}
                      </button>
                      {!isEditing && (
                        <button onClick={() => startEdit(r)} style={{
                          padding: '4px 12px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
                          background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: 11,
                        }}>✏️ Edit</button>
                      )}
                      {r.disputed && (
                        <button onClick={() => resolveDispute(r.id)} title="Clear the disputed flag once you've corrected this entry or confirmed it's right"
                          style={{
                            padding: '4px 12px', borderRadius: 8, border: '1px solid #bbf7d0', cursor: 'pointer',
                            background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: 11,
                          }}>✓ Mark resolved</button>
                      )}
                    </div>

                    {isThreadOpen && (
                      <LeaveRequestThread company={company} requestId={r.id} currentUser={currentUser} />
                    )}
                  </div>
                  {r.status === 'pending' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220 }}>
                      <input
                        placeholder="Admin note (optional)"
                        value={adminNote[r.id] || ''}
                        onChange={e => setAdminNote(n => ({ ...n, [r.id]: e.target.value }))}
                        style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, width: '100%', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleAction(r.id, 'approved', adminNote[r.id])} style={{
                          flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                          background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 12,
                        }}>✓ Approve</button>
                        <button onClick={() => handleAction(r.id, 'rejected', adminNote[r.id])} style={{
                          flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                          background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 12,
                        }}>✕ Reject</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Policy Editor ────────────────────────────────────────────────────────────
function PolicyEditor({ policy, setPolicy, company }) {
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_POLICY, ...policy }));
  const [saving, setSaving] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => { setDraft({ ...DEFAULT_POLICY, ...policy }); }, [policy]);

  const savePolicy = async () => {
    if (!company?.id) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'companies', company.id, 'leavePolicy', 'default'), {
        ...draft,
        updatedAt: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const addHoliday = () => {
    if (!newHoliday.date || !newHoliday.name.trim()) return;
    setDraft(d => ({
      ...d,
      holidays: [...(d.holidays || []), { date: newHoliday.date, name: newHoliday.name.trim() }]
        .sort((a, b) => a.date.localeCompare(b.date)),
    }));
    setNewHoliday({ date: '', name: '' });
  };

  const removeHoliday = (idx) => {
    setDraft(d => ({ ...d, holidays: d.holidays.filter((_, i) => i !== idx) }));
  };

  // Predefined-holiday picker: a checkbox is "on" when an exact {date, name}
  // match already sits in draft.holidays. Toggling adds/removes that one
  // entry without touching any manually-typed holidays, even ones that
  // happen to fall on the same date under a different name.
  const isPredefinedSelected = (h) => (draft.holidays || []).some(x => x.date === h.date && x.name === h.name);

  const togglePredefinedHoliday = (h) => {
    setDraft(d => {
      const exists = (d.holidays || []).some(x => x.date === h.date && x.name === h.name);
      const holidays = exists
        ? d.holidays.filter(x => !(x.date === h.date && x.name === h.name))
        : [...(d.holidays || []), { date: h.date, name: h.name }].sort((a, b) => a.date.localeCompare(b.date));
      return { ...d, holidays };
    });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* Leave allocations */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 20 }}>Leave Allocations (Annual)</div>
        {[
          { key: 'annualLeaves', label: '🏖️ Annual Leave',  color: '#7c3aed' },
          { key: 'sickLeaves',   label: '🤒 Sick Leave',    color: '#ef4444' },
          { key: 'casualLeaves', label: '☕ Casual Leave',   color: '#f59e0b' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>{f.label}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="number" min="0" max="365" value={draft[f.key] || 0}
                onChange={e => setDraft(d => ({ ...d, [f.key]: parseInt(e.target.value) || 0 }))}
                style={{ width: 70, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, fontWeight: 700, textAlign: 'center' }}
              />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>days per year</span>
            </div>
          </div>
        ))}
        <button onClick={savePolicy} disabled={saving} style={{
          marginTop: 8, padding: '10px 24px', borderRadius: 9, border: 'none', cursor: 'pointer',
          background: 'var(--color-primary, #7c3aed)', color: '#fff', fontWeight: 700, fontSize: 13,
        }}>{saving ? 'Saving…' : 'Save Policy'}</button>
      </div>

      {/* National Holidays */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>National Holidays</div>
          <button onClick={() => setShowPicker(s => !s)} style={{
            padding: '6px 12px', borderRadius: 7, border: '1px solid #ddd6fe', cursor: 'pointer',
            background: showPicker ? '#7c3aed' : '#f5f3ff', color: showPicker ? '#fff' : '#7c3aed', fontWeight: 700, fontSize: 11.5,
          }}>{showPicker ? 'Hide India 2026 List' : '📋 Pick from India 2026 List'}</button>
        </div>

        {showPicker && (
          <div style={{ marginBottom: 18, border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#fafafe' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12, lineHeight: 1.5 }}>
              Tick the holidays your company wants to observe — nothing is added until you check it. National &amp;
              Gazetted holidays are the ones most companies follow; the Popular Festivals section below is fully
              optional. Dates marked "tentative" follow the lunar calendar and may shift by a day once the
              government's official notification comes out.
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {['national', 'gazetted', 'restricted'].map(cat => (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    {HOLIDAY_CATEGORY_LABELS[cat]}
                  </div>
                  {INDIA_HOLIDAYS_2026.filter(h => h.category === cat).map(h => {
                    const checked = isPredefinedSelected(h);
                    return (
                      <label key={h.date + h.name} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px',
                        borderRadius: 7, cursor: 'pointer', background: checked ? '#f5f3ff' : 'transparent',
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => togglePredefinedHoliday(h)} />
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{h.name}</span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>{formatDate(h.date)}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input type="date" value={newHoliday.date} onChange={e => setNewHoliday(h => ({ ...h, date: e.target.value }))}
            style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
          <input placeholder="Holiday name" value={newHoliday.name} onChange={e => setNewHoliday(h => ({ ...h, name: e.target.value }))}
            style={{ flex: 2, padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
          <button onClick={addHoliday} style={{
            padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: 'var(--color-primary, #7c3aed)', color: '#fff', fontWeight: 700, fontSize: 12,
          }}>+</button>
        </div>
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          {(draft.holidays || []).length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 20 }}>No holidays added yet</div>
          ) : (draft.holidays || []).map((h, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              borderRadius: 8, marginBottom: 4, background: '#f8fafc',
            }}>
              <span style={{ fontSize: 16 }}>🎉</span>
              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{h.name}</span>
              <span style={{ fontSize: 11, color: '#64748b' }}>{formatDate(h.date)}</span>
              <button onClick={() => removeHoliday(i)} style={{
                border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: '0 4px',
              }}>×</button>
            </div>
          ))}
        </div>
        {(draft.holidays || []).length > 0 && (
          <button onClick={savePolicy} disabled={saving} style={{
            marginTop: 12, padding: '8px 20px', borderRadius: 9, border: 'none', cursor: 'pointer',
            background: 'var(--color-primary, #7c3aed)', color: '#fff', fontWeight: 700, fontSize: 12,
          }}>{saving ? 'Saving…' : 'Save Holidays'}</button>
        )}
      </div>
    </div>
  );
}

// ─── Team Leave View (admin overview per user) ────────────────────────────────
// Lets an admin back-fill a leave for any team member, for a past OR future
// date range, outside the normal employee-submits/admin-approves flow — e.g.
// leave the employee took before this system was in use, or a future leave
// that's already been verbally agreed and should reduce their balance now so
// the "available leaves" figure they see is accurate. The logged record is
// written straight into the same `leaveRequests` collection with
// status: 'approved', so it flows through the exact same balance calculation
// every other approved request already uses (MyLeavePortal's usedByType) —
// no separate "adjustment" math to keep in sync.
function TeamLeaveView({ teamMembers, requests, policy, company, currentUser }) {
  const { logActivity } = useCRM();
  const [editMember, setEditMember] = useState(null);
  const [joiningDate, setJoiningDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [logLeaveMember, setLogLeaveMember] = useState(null); // the member currently being logged for
  const [logLeaveForm, setLogLeaveForm] = useState({ type: 'annual', from: '', to: '', isHalfDay: false, reason: '' });
  const [loggingLeave, setLoggingLeave] = useState(false);

  const memberStats = useMemo(() => {
    return teamMembers.map(m => {
      const memberReqs = requests.filter(r => r.userId === m.id && r.status === 'approved');
      const used = { annual: 0, sick: 0, casual: 0 };
      memberReqs.forEach(r => { if (used[r.type] !== undefined) used[r.type] += r.days || 0; });
      return { ...m, used };
    });
  }, [teamMembers, requests]);

  const saveJoiningDate = async () => {
    if (!company?.id || !editMember) return;
    setSaving(true);
    try {
      // Member docs live at the top-level `users/{uid}` path (see
      // firebase/firestore.js), not `companies/{id}/users/{uid}` — this was
      // silently writing to a doc that doesn't exist anywhere else in the app.
      await updateUserProfile(editMember.id, { joiningDate });
      setEditMember(null);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const openLogLeave = (member) => {
    setLogLeaveMember(member);
    setLogLeaveForm({ type: 'annual', from: '', to: '', isHalfDay: false, reason: '' });
  };

  const saveLoggedLeave = async () => {
    if (!company?.id || !logLeaveMember) return;
    const to = logLeaveForm.isHalfDay ? logLeaveForm.from : logLeaveForm.to;
    if (!logLeaveForm.from || !to) return alert('Please pick the date(s) for this leave');
    setLoggingLeave(true);
    try {
      const days = computeRequestDays(logLeaveForm.from, to, logLeaveForm.isHalfDay);
      await addDoc(collection(db, 'companies', company.id, 'leaveRequests'), {
        userId: logLeaveMember.id,
        userName: logLeaveMember.name || logLeaveMember.email,
        userEmail: logLeaveMember.email,
        type: logLeaveForm.type,
        from: logLeaveForm.from,
        to,
        days,
        isHalfDay: !!logLeaveForm.isHalfDay,
        reason: logLeaveForm.reason.trim() || 'Logged by admin',
        // Admin-logged leaves are recorded as already-decided, not a pending
        // request — they go straight to 'approved' so the employee's balance
        // reflects them immediately, whether the date is in the past or future.
        status: 'approved',
        createdAt: serverTimestamp(),
        reviewedAt: serverTimestamp(),
        adminNote: logLeaveForm.reason.trim() || 'Logged by admin',
        loggedByAdmin: true,
        loggedByAdminId: currentUser?.id || null,
        loggedByAdminName: currentUser?.name || currentUser?.email || 'Admin',
        // Lets the employee see on their own "My Leaves" tab that something
        // was added on their behalf, same unread signal used elsewhere.
        employeeUnread: true,
      });
      logActivity && logActivity('leave_logged_by_admin', null, logLeaveMember.name || logLeaveMember.email, {
        employeeName: logLeaveMember.name || logLeaveMember.email,
        type: logLeaveForm.type,
        from: logLeaveForm.from,
        to,
        days,
      });
      setLogLeaveMember(null);
    } catch (e) { console.error(e); alert('Failed to log leave'); }
    setLoggingLeave(false);
  };

  const logLeaveDaysCalc = logLeaveForm.isHalfDay
    ? (logLeaveForm.from ? 0.5 : 0)
    : (logLeaveForm.from && logLeaveForm.to ? daysBetween(logLeaveForm.from, logLeaveForm.to) : 0);

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
        Team Leave Overview
      </div>
      {memberStats.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No team members found</div>
      ) : memberStats.map(m => (
        <div key={m.id} style={{ padding: '16px 20px', borderBottom: '1px solid #f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Avatar */}
            <div style={{
              width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
              background: m.avatar?.bg || '#e2e8f0',
              color: m.avatar?.color || '#0f172a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700,
            }}>
              {(m.name || m.email || 'U').slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{m.name || m.email}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                {m.role} · Joined: {m.joiningDate ? formatDate(m.joiningDate) : <span style={{ color: '#f59e0b' }}>Not set</span>}
              </div>
            </div>
            {/* Leave usage bars */}
            <div style={{ display: 'flex', gap: 14 }}>
              {[
                { id: 'annual', total: policy.annualLeaves ?? 20, color: '#7c3aed', label: 'Annual' },
                { id: 'sick',   total: policy.sickLeaves   ?? 10, color: '#ef4444', label: 'Sick' },
                { id: 'casual', total: policy.casualLeaves  ?? 6, color: '#f59e0b', label: 'Casual' },
              ].map(t => {
                const used = m.used[t.id] || 0;
                return (
                  <div key={t.id} style={{ width: 70, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>{t.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.color }}>{used}<span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>/{t.total}</span></div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => { setEditMember(m); setJoiningDate(m.joiningDate || ''); setLogLeaveMember(null); }} style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
              background: '#f8fafc', color: '#64748b', fontSize: 11, fontWeight: 600,
            }}>Set Joining Date</button>
            <button onClick={() => { if (logLeaveMember?.id === m.id) { setLogLeaveMember(null); } else { openLogLeave(m); setEditMember(null); } }} style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #ddd6fe', cursor: 'pointer',
              background: logLeaveMember?.id === m.id ? '#7c3aed' : '#f5f3ff',
              color: logLeaveMember?.id === m.id ? '#fff' : '#7c3aed', fontSize: 11, fontWeight: 700,
            }}>🗓️ Log Leave</button>
          </div>
          {editMember?.id === m.id && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 52 }}>
              <input type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <button onClick={saveJoiningDate} disabled={saving} style={{
                padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'var(--color-primary, #7c3aed)', color: '#fff', fontWeight: 700, fontSize: 12,
              }}>{saving ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setEditMember(null)} style={{
                padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
                background: '#f8fafc', color: '#64748b', fontSize: 12,
              }}>Cancel</button>
            </div>
          )}

          {/* Admin: log a past or future leave directly for this employee, so
              their available-leave balance reflects reality even for leave
              taken/agreed outside the normal request flow. */}
          {logLeaveMember?.id === m.id && (
            <div style={{ marginTop: 12, padding: 14, borderRadius: 10, background: '#fafafe', border: '1px solid #ddd6fe', marginLeft: 52 }}>
              <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 700, marginBottom: 10 }}>
                Log a leave for {m.name || m.email} — past or future date
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <select value={logLeaveForm.type} onChange={e => setLogLeaveForm(f => ({ ...f, type: e.target.value }))}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}>
                  {LEAVE_TYPES.map(lt => <option key={lt.id} value={lt.id}>{lt.icon} {lt.label}</option>)}
                </select>
                <input type="date" value={logLeaveForm.from}
                  onChange={e => setLogLeaveForm(f => ({ ...f, from: e.target.value, to: f.isHalfDay ? e.target.value : f.to }))}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <input type="date" value={logLeaveForm.isHalfDay ? logLeaveForm.from : logLeaveForm.to}
                  min={logLeaveForm.from} disabled={logLeaveForm.isHalfDay}
                  onChange={e => setLogLeaveForm(f => ({ ...f, to: e.target.value }))}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, background: logLeaveForm.isHalfDay ? '#f1f5f9' : '#fff' }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                  <input type="checkbox" checked={logLeaveForm.isHalfDay}
                    onChange={e => setLogLeaveForm(f => ({ ...f, isHalfDay: e.target.checked, to: e.target.checked ? f.from : f.to }))} />
                  Half day
                </label>
              </div>
              <input placeholder="Reason / note (optional)" value={logLeaveForm.reason}
                onChange={e => setLogLeaveForm(f => ({ ...f, reason: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, marginBottom: 10, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
                Will record: <strong>{formatDays(logLeaveDaysCalc)}</strong> — counted as <strong>approved</strong> immediately,
                reducing {m.name || m.email}'s available balance right away (works for past or future dates).
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveLoggedLeave} disabled={loggingLeave} style={{
                  padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12,
                }}>{loggingLeave ? 'Saving…' : 'Log Leave'}</button>
                <button onClick={() => setLogLeaveMember(null)} style={{
                  padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
                  background: '#fff', color: '#64748b', fontSize: 12,
                }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main Leaves Page ─────────────────────────────────────────────────────────
export default function Leaves() {
  // leaveRequests now comes from the centralized CRMContext subscription
  // (added so Header.js's notification bell can see pending-count and
  // adminUnread/employeeUnread flags app-wide, not just while this page is
  // mounted). This page no longer keeps its own duplicate onSnapshot listener.
  // `users` is the same company-scoped roster CRMContext already subscribes
  // to (top-level `users` collection, filtered by companyId — see
  // firebase/firestore.js subscribeUsers). Team Leave Overview previously
  // kept its own separate onSnapshot listener pointed at
  // `companies/{id}/users`, a subcollection that's never actually written to
  // anywhere in the app — every member doc lives at the top-level `users`
  // path instead — so that listener always resolved to an empty array and
  // the admin saw "No team members found" no matter how many counsellors
  // existed. Reusing the already-correct `users` list fixes it.
  const { currentUser, company, leads, users, leaveRequests: requests } = useCRM();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const teamMembers = users || [];
  const [view, setView] = useState(isAdmin ? 'admin' : 'my'); // 'my' | 'admin'

  // Load leave policy
  useEffect(() => {
    if (!company?.id) return;
    const unsub = onSnapshot(doc(db, 'companies', company.id, 'leavePolicy', 'default'), snap => {
      if (snap.exists()) setPolicy({ ...DEFAULT_POLICY, ...snap.data() });
    });
    return unsub;
  }, [company?.id]);

  const myPendingCount = requests.filter(r => r.userId === currentUser?.id && r.status === 'pending').length;
  const adminPendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div style={{ height: '100%', overflowY: 'auto', boxSizing: 'border-box', background: 'var(--bg-secondary, #f8fafc)' }}>
      {/* Top nav tabs */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        padding: '0 28px', display: 'flex', alignItems: 'center', gap: 0,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button onClick={() => setView('my')} style={{
          padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 600,
          color: view === 'my' ? 'var(--color-primary, #7c3aed)' : '#64748b',
          borderBottom: view === 'my' ? '2px solid var(--color-primary, #7c3aed)' : '2px solid transparent',
        }}>
          My Leaves {myPendingCount > 0 && <span style={{ marginLeft: 4, background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 6px' }}>{myPendingCount}</span>}
        </button>
        {isAdmin && (
          <button onClick={() => setView('admin')} style={{
            padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            color: view === 'admin' ? 'var(--color-primary, #7c3aed)' : '#64748b',
            borderBottom: view === 'admin' ? '2px solid var(--color-primary, #7c3aed)' : '2px solid transparent',
          }}>
            Admin Panel {adminPendingCount > 0 && <span style={{ marginLeft: 4, background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 6px' }}>{adminPendingCount}</span>}
          </button>
        )}
      </div>

      {/* Content */}
      {view === 'my' && (
        <MyLeavePortal
          requests={requests}
          policy={policy}
          currentUser={currentUser}
        />
      )}
      {view === 'admin' && isAdmin && (
        <AdminApprovalPanel
          requests={requests}
          policy={policy}
          setPolicy={setPolicy}
          company={company}
          teamMembers={teamMembers}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}
